import { last } from 'lodash';

import { Cell } from '@/explore/types';

import { InvalidPipelineReferenceError } from '@/explore/utils/validation';

import {
  getConversationCells,
  isConversationPart,
  isStartOfConversation,
} from './conversation-cell';
import { CellWithPipeline, isCellWithPipeline, isVariableCell, setCellPipeline } from '.';
import { removeReferenceToParentPipeline } from '../pipeline';

export enum CellKindByOperation {
  funnel = 'funnel',
  cohort = 'cohort',
  sql = 'sql',
  python = 'python',
}

export const getCell = (cells: Cell[], cellId: string): Cell | undefined =>
  cells.find((cell) => cell.id === cellId);

export const getCellByIndex = (cells: Cell[], index: number) => cells.at(index);

export const getCellIndex = (cells: Cell[], cellId: string) =>
  cells.findIndex((cell) => cell.id === cellId);

export const getCellByPipelineId = (
  cells: Cell[],
  pipelineId: string,
): CellWithPipeline | undefined =>
  cells.filter(isCellWithPipeline).find((cell) => cell.pipeline.pipelineId === pipelineId);

export const getCellByPipelineIdOrThrow = (cells: Cell[], pipelineId: string) => {
  const cell = getCellByPipelineId(cells, pipelineId);
  if (cell === undefined) {
    throw new InvalidPipelineReferenceError(`Could not find cell with pipeline id ${pipelineId}`);
  }
  return cell;
};

/**
 * Add cells at a specific index. This function will filter out duplicate variable cells.
 * @param cells Existing cells.
 * @param insertedCells Cells to add.
 * @param idx Index to add at.
 * @returns New array of cells with the added cells.
 */
export const addCellsAt = (cells: Cell[], insertedCells: Cell[], idx: number): Cell[] => [
  ...cells.slice(0, idx),
  ...insertedCells.filter(filterVariableCellDuplicates(cells)),
  ...cells.slice(idx),
];

export const addCellAt = (cells: Cell[], insertedCell: Cell, idx: number): Cell[] =>
  addCellsAt(cells, [insertedCell], idx);

export const addCells = (cells: Cell[], addedCells: Cell[], afterCellId: string): Cell[] => {
  const idx = cells.findIndex((cell) => cell.id === afterCellId);
  return addCellsAt(cells, addedCells, idx + 1);
};

export const addCell = (cells: Cell[], addedCell: Cell, afterCellId: string): Cell[] =>
  addCells(cells, [addedCell], afterCellId);

export const replaceCell = (cells: Cell[], cell: Cell, replacedCellId: string): Cell[] =>
  cells.map((existingCell) => (existingCell.id === replacedCellId ? cell : existingCell));

export const deleteCell = (cells: Cell[], cellId: string): Cell[] => {
  const deletedCell = getCell(cells, cellId);

  if (deletedCell === undefined) {
    throw new Error(`Could not find cell with id ${cellId}`);
  }

  return cells
    .filter((cell) => cell.id !== cellId)
    .map((cell) => {
      if (!isCellWithPipeline(cell) || !isCellWithPipeline(deletedCell)) {
        return cell;
      }

      return setCellPipeline(
        cell,
        removeReferenceToParentPipeline(cell.pipeline, deletedCell.pipeline),
      );
    });
};

/**
 * Filter function to prevent adding duplicate variable cells.
 * @param existingCells Existing cells to compare against.
 * @returns A function that returns true if it is safe to add the new cell.
 */
const filterVariableCellDuplicates =
  (existingCells: Cell[]) =>
  (newCell: Cell): boolean =>
    isVariableCell(newCell)
      ? !existingCells.some(
          (existingCell) =>
            isVariableCell(existingCell) && existingCell.definition.key === newCell.definition.key,
        )
      : true;

/**
 * Find the next cell, after which, we can insert a new cell ensuring that
 * we don't break rows or a conversation subject from the conversation.
 */
export const getNextInsertPoint = (cells: Cell[], afterCellId: string): string => {
  const afterCell = getCell(cells, afterCellId);
  if (afterCell === undefined) {
    throw new Error('Cannot find cell');
  }

  if (isConversationPart(afterCell)) {
    const lastConversationCell = last(getConversationCells(cells, afterCell.conversationId));
    if (lastConversationCell === undefined) {
      throw new Error('Cannot find last conversation cell');
    }
    return lastConversationCell.id;
  }

  const cellIndex = getCellIndex(cells, afterCellId);
  const rowEndIndex =
    cellIndex +
    Math.max(
      0,
      cells
        .slice(cellIndex + 1)
        .findIndex((cell) => cell.viewOptions?.rowId !== afterCell.viewOptions?.rowId),
    );

  const insertIndex = rowEndIndex + (isStartOfConversation(afterCell, cells) ? 1 : 0);
  const cellAtIndex = cells.at(insertIndex);

  if (cellAtIndex === undefined) {
    throw new Error('Cannot find cell at insert index');
  }

  return cellAtIndex.id;
};
